/**
 * Thin fetch wrapper for the Bankom Portal API.
 *
 * Always sends `credentials: 'include'` so the httpOnly auth cookies travel
 * with every request. On 401 we transparently try `/api/auth/refresh` once
 * and re-issue the original request — that hides the 15-minute access-token
 * rotation from the rest of the app.
 *
 * This is intentionally lightweight (no React Query, no axios) because the
 * portal needs few client-side caching tricks. We can layer them in later.
 */

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details?: string[];
};

const API_BASE = "/api";

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Called once when a request 401s and the token refresh also fails — i.e. the
 * session is truly gone (refresh token expired/revoked). AuthContext registers
 * a handler that flips the app to anonymous so route guards redirect to /login
 * instead of widgets polling 401s against a dead session forever.
 */
let sessionExpiredHandler: (() => void) | null = null;
let sessionExpiredNotified = false;

export function setSessionExpiredHandler(fn: (() => void) | null) {
  sessionExpiredHandler = fn;
  sessionExpiredNotified = false;
}

function notifySessionExpired() {
  if (sessionExpiredNotified) return; // one burst of concurrent 401s → one notification
  sessionExpiredNotified = true;
  sessionExpiredHandler?.();
}

/** Re-arm the expiry notification after a successful login/refresh. */
export function resetSessionExpiredNotice() {
  sessionExpiredNotified = false;
}

async function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        // Reset on next tick so callers piggybacking on the same in-flight refresh
        // all observe the same outcome.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });
  }
  return refreshInFlight;
}

async function readError(res: Response): Promise<ApiError> {
  let body: Partial<ApiError> = {};
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    /* ignore — non-JSON error */
  }
  const code = body.code ?? `HTTP_${res.status}`;
  return {
    status: res.status,
    code,
    // The backend's 403 texts are English ("Access denied") and screens show `message` as is.
    message: code === "FORBIDDEN"
      ? FORBIDDEN_MESSAGE
      : code === "OUT_OF_SCOPE"
        ? OUT_OF_SCOPE_MESSAGE
        : (body.message ?? res.statusText),
    details: body.details,
  };
}

const FORBIDDEN_MESSAGE = "Nemate pristup ovom delu.";
/** 403 outside the user's structure scope (sprint 7 §12). */
const OUT_OF_SCOPE_MESSAGE = "Ova pozicija je van vašeg opsega izmena strukture.";

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** When true, do not attempt token refresh on 401 (use for /auth/login itself). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
};

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  };

  let res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 401 && !opts.skipRefresh) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, init);
    } else {
      notifySessionExpired();
    }
  }

  if (!res.ok) {
    throw await readError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/**
 * Sends one file as `multipart/form-data` (field `file`) and reads the JSON answer: same
 * cookies and the same one-shot token refresh as `apiFetch`. The browser sets the content
 * type itself — it has to carry the part boundary.
 */
export async function apiUpload<T = unknown>(path: string, file: File): Promise<T> {
  const send = () => {
    const form = new FormData();
    form.append("file", file, file.name);
    return fetch(`${API_BASE}${path}`, { method: "POST", credentials: "include", headers: { Accept: "application/json" }, body: form });
  };
  let res = await send();
  if (res.status === 401) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await send();
    } else {
      notifySessionExpired();
    }
  }
  if (!res.ok) {
    throw await readError(res);
  }
  return (await res.json()) as T;
}

/**
 * Fetches a file the server builds on request (exports): same cookies and the
 * same one-shot token refresh as `apiFetch`, but the body is a blob. `filename`
 * is what the server put in `Content-Disposition`, null when it sent none.
 */
export async function apiDownload(path: string, body?: unknown): Promise<{ blob: Blob; filename: string | null }> {
  // A body means the export takes structured criteria (the advanced search) — sent as POST.
  const init: RequestInit = body === undefined
    ? { method: "GET", credentials: "include" }
    : { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
  let res = await fetch(`${API_BASE}${path}`, init);
  if (res.status === 401) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, init);
    } else {
      notifySessionExpired();
    }
  }
  if (!res.ok) {
    throw await readError(res);
  }
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match ? match[1] : null };
}

/**
 * Builds a `?a=1&b=x` query string, skipping undefined/null/empty values so
 * callers can pass optional filters straight through. Returns "" when nothing
 * survives the filter.
 */
export function buildQuery(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}
